import React, { useState, useEffect } from 'react';
import WebFont from 'webfontloader';
import FontPreview from './FontPreview';

interface Font {
  name: string;
  fontFamily: string;
}

interface GenerateSignProps {
  customWidth?: number;
  customHeight?: number;
  signatureUrl?: (url: string) => void;
}

const GenerateSign: React.FC<GenerateSignProps> = (props) => {
  const [name, setName] = useState<string>('');
  const [selectedSignature, setSelectedSignature] = useState<number | null>(null);

  const signatureFonts: Font[] = [
    { name: 'Great Vibes', fontFamily: 'Great Vibes' },
    { name: 'Pacifico', fontFamily: 'Pacifico' },
    { name: 'Sacramento', fontFamily: 'Sacramento' },
    { name: 'Dancing Script', fontFamily: 'Dancing Script' },
    { name: 'Satisfy', fontFamily: 'Satisfy' },
    { name: 'Allura', fontFamily: 'Allura' },
    { name: 'Alex Brush', fontFamily: 'Alex Brush' },
    { name: 'Parisienne', fontFamily: 'Parisienne' },
    { name: 'Marck Script', fontFamily: 'Marck Script' },
    { name: 'Kaushan Script', fontFamily: 'Kaushan Script' },
    { name: 'Italianno', fontFamily: 'Italianno' },
    { name: 'Mr Dafoe', fontFamily: 'Mr Dafoe' },
    { name: 'Mr Bedfort', fontFamily: 'Mr Bedfort' },
    { name: 'Tangerine', fontFamily: 'Tangerine' },
    { name: 'Yellowtail', fontFamily: 'Yellowtail' },
    { name: 'Qwigley', fontFamily: 'Qwigley' },
    { name: 'Herr Von Muellerhoff', fontFamily: 'Herr Von Muellerhoff' },
    { name: 'Petit Formal Script', fontFamily: 'Petit Formal Script' },
    { name: 'Cookie', fontFamily: 'Cookie' },
    { name: 'League Script', fontFamily: 'League Script' },
    { name: 'Corinthia', fontFamily: 'Corinthia' },
    { name: 'Arizonia', fontFamily: 'Arizonia' },
    { name: 'Rouge Script', fontFamily: 'Rouge Script' },
    { name: 'Clicker Script', fontFamily: 'Clicker Script' },
    { name: 'La Belle Aurore', fontFamily: 'La Belle Aurore' },
    { name: 'Pinyon Script', fontFamily: 'Pinyon Script' },
    { name: 'Meow Script', fontFamily: 'Meow Script' },
    { name: 'Bad Script', fontFamily: 'Bad Script' },
    { name: 'Meddon', fontFamily: 'Meddon' },
    { name: 'Delius Swash Caps', fontFamily: 'Delius Swash Caps' },
    { name: 'MonteCarlo', fontFamily: 'MonteCarlo' },
    { name: 'Norican', fontFamily: 'Norican' },
    { name: 'Yesteryear', fontFamily: 'Yesteryear' },
    { name: 'Loved by the King', fontFamily: 'Loved by the King' },
    { name: 'Bilbo Swash Caps', fontFamily: 'Bilbo Swash Caps' },
    { name: 'Kristi', fontFamily: 'Kristi' },
    { name: 'Gochi Hand', fontFamily: 'Gochi Hand' },
    { name: 'Rock Salt', fontFamily: 'Rock Salt' },
    { name: 'Nanum Brush Script', fontFamily: 'Nanum Brush Script' },
    { name: 'Homemade Apple', fontFamily: 'Homemade Apple' },
    { name: 'Just Me Again Down Here', fontFamily: 'Just Me Again Down Here' },
    { name: 'Reenie Beanie', fontFamily: 'Reenie Beanie' },
    { name: 'Zeyada', fontFamily: 'Zeyada' },
    { name: 'Waiting for the Sunrise', fontFamily: 'Waiting for the Sunrise' },
    { name: 'Give You Glory', fontFamily: 'Give You Glory' },
    { name: 'Over the Rainbow', fontFamily: 'Over the Rainbow' },
    { name: 'Nothing You Could Do', fontFamily: 'Nothing You Could Do' },
    { name: 'Handlee', fontFamily: 'Handlee' },
    { name: 'Indie Flower', fontFamily: 'Indie Flower' },
    { name: 'Sue Ellen Francisco', fontFamily: 'Sue Ellen Francisco' },
  ];

  useEffect(() => {
    WebFont.load({
      google: {
        families: signatureFonts.map((font) => font.fontFamily),
      },
      active: () => console.log('Fonts loaded'),
      inactive: () => console.error('Fonts failed to load'),
    });
  }, []);

  const exportSignatureToImage = (index: number) => {
    setSelectedSignature(index);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    const width = props?.customWidth ?? 400;
    const height = props?.customHeight ?? 100;
    canvas.width = width;
    canvas.height = height;

    const font = signatureFonts[index];
    const fontSize = 48;
    ctx.clearRect(0, 0, width, height);

    ctx.font = `${fontSize}px ${font.fontFamily}`;
    ctx.fillStyle = '#000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const yOffset = height / 2;
    ctx.fillText(name, width / 2, yOffset);

    const dataUrl = canvas.toDataURL('image/png');
    props?.signatureUrl?.(dataUrl);
  };

  return (
    <div style={{ padding: '1rem' }}>
      <input
        type="text"
        placeholder="Type here....."
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={{
          border: '1px solid #ccc',
          padding: '0.5rem',
          width: '100%',
          marginBottom: '1rem',
        }}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem',
          maxHeight: '200px',
          overflowY: 'auto',
        }}
      >
        {signatureFonts.map((font, index) => (
          <div
            key={index}
            onClick={() => exportSignatureToImage(index)}
            style={{
              padding: '0.5rem',
              border: selectedSignature === index ? '2px solid #6b21a8' : '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '24px',
                width: '150px',
                height: '45px',
                overflowX: 'auto',
                margin: '0 auto',
              }}
            >
              <FontPreview font={font} name={name} />
            </div>
            <div style={{ fontSize: '12px' }}>{font.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GenerateSign;
